import * as React from 'react';
import styled from 'styled-components';
import { useRef } from 'react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger, RoughEase } from 'gsap/all';
import ScrollHintArrow from '../ScrollHintArrow';

gsap.registerPlugin(ScrollTrigger, RoughEase);

// ───────────────────────────────────────
// Styled Components
// ───────────────────────────────────────
const HeaderSection = styled.div`
  position: relative;
  background-color: #000000;
  background-image: url('https://assets.volkswagen.com/is/image/volkswagenag/header-background-desktop?Zml0PWNyb3AlMkMxJndpZD0yNTYwJmhlaT0xNDQwJmZtdD1qcGVnJnFsdD03OSZiZmM9b2ZmJjRhNjQ=');
  background-size: cover;
  background-position: center;
  height: 100vh;
  width: 100%;
  overflow: hidden;
  transform: translateY(1px);

  @media screen and (max-width: 860px) {
    background-position: 45% center;
    background-image: url('https://assets.volkswagen.com/is/image/volkswagenag/stage_mobile?Zml0PWNyb3AlMkMxJndpZD0xMTI1JmhlaT0yMDAwJmZtdD1qcGVnJnFsdD03OSZiZmM9b2ZmJmMwMWQ=');
  }
`;

const HeaderTitle = styled.div`
  color: #c90010;
  height: 92vh;
  width: 40vw;
  margin-top: 8vh;
  font-size: 20vh;
  font-family: 'Futura Condensed Extra Bold', Futura, Arial, sans-serif;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: -1vh;
  line-height: 16vh;
  margin-left: 5vw;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;

  @media screen and (max-width: 1050px) {
    font-size: 12vw;
    line-height: 9vw;
    letter-spacing: -0.75vw;
    height: 75vh;
    margin-top: 0;
  }

  @media screen and (max-width: 560px) {
  }
`;

const TextLineWrapper = styled.div`
  position: relative;
  margin-bottom: 1vh;
`;

const RevealOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 110%;
  height: 100%;
  background-color: #c90010;
  transform-origin: left;

  @media screen and (max-width: 560px) {
    top: -5%;
    width: 110%;
    height: 115%;
  }
`;

const HeaderSign = styled.img`
  position: absolute;
  right: 3vw;
  bottom: 5vh;
  height: 60vh;

  @media screen and (max-width: 1050px) {
    left: 10vw;
    height: auto;
    width: 80vw;
    top: 55vh;
  }
`;

// ───────────────────────────────────────
// Header Component
// ───────────────────────────────────────
export const Header = (): React.ReactElement => {
  const headerTitleRef = useRef<HTMLDivElement>(null);
  const headerLineRefs = useRef<Array<HTMLDivElement | null>>([]);
  const overlayRefs = useRef<Array<HTMLDivElement | null>>([]);
  const headerDriverRef = useRef<HTMLImageElement>(null);
  const headerSignRef = useRef<HTMLImageElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  const texts = ['Todos', 'los', 'coches', 'tienen', 'motor'];

  useGSAP(() => {
    const introTL = gsap.timeline();

    // Preparar overlays
    introTL.add(() => {
      overlayRefs.current.forEach((overlay) => {
        if (overlay) {
          gsap.set(overlay, { scaleX: 1 });
        }
      });
    }, 0);

    // ⏱️ Delay inicial antes de animar
    introTL.add('revealStart', 0.5);

    // Revelado de cada línea + inercia del texto
    overlayRefs.current.forEach((overlay, i) => {
      const line = headerLineRefs.current[i];
      if (overlay && line) {
        const t = `revealStart+=${i * 0.1}`;

        // Movimiento de inercia
        introTL.fromTo(
          line,
          { x: 15 },
          {
            x: 0,
            duration: 0.2,
            ease: 'power1.out',
          },
          t,
        );

        // Retirada de caja
        introTL.to(
          overlay,
          {
            scaleX: 0,
            duration: 0.2,
            ease: 'power1.out',
          },
          t,
        );
      }
    });

    // Continuación
    introTL.to({}, { duration: 0.3 });

    introTL.fromTo(
      headerDriverRef.current,
      { opacity: 0, y: 30 },
      { opacity: 1, y: 0, duration: 1.5, ease: 'power2.out' },
    );

    // Start GIF before title animation completes
    introTL.call(
      () => {
        if (headerSignRef.current) {
          headerSignRef.current.src =
            'https://vgedonehubappsstaweupro.blob.core.windows.net/storage/images-gti50/no-todos-tienen-alma.gif';
          headerSignRef.current.style.opacity = '1';
        }
      },
      [],
      '-=1.5',
    );
  }, []);

  return (
    <HeaderSection className="header-section" ref={sectionRef}>
      <HeaderTitle className="header-title" ref={headerTitleRef}>
        {texts.map((text, i) => (
          <TextLineWrapper key={i} ref={(el) => (headerLineRefs.current[i] = el)}>
            {text}
            <RevealOverlay ref={(el) => (overlayRefs.current[i] = el)} />
          </TextLineWrapper>
        ))}
      </HeaderTitle>
      <HeaderSign
        ref={headerSignRef}
        className="header-sign"
        src=""
        alt="Header sign animation"
        style={{ opacity: 0 }}
      />
      <ScrollHintArrow />
    </HeaderSection>
  );
};
